import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAppContext } from '../../../../../app/Context';
import { publishPoll, savePublishPreferences } from '../../../../../db/polls';
import { Poll, StudentResponses } from '../../../../../db/polls/types';
import { ActivityDuration, DueDateType, ToBeDone } from '../../../../../db/types';
import { i18nNS } from '../../../../../i18n';
import routes, { PollParams } from '../../../../../routes/router';
import { format } from '../../../../../utils/datetime';
import { toShortId } from '../../../../../utils/helper';
import { useClassContext } from '../../../../class/Context';
import { useCourseContext } from '../../../../course/Context';
import { usePollContext } from '../../../Context';
import { FormValues } from './types';

export interface Props {
  open: boolean;
  onClose: () => void;
  isPublishing?: boolean;
}

const useEditPublishPreferencesDrawerVM = ({ open, onClose, isPublishing }: Props) => {
  const { t } = useTranslation([i18nNS.POLL, i18nNS.COMMON]);
  const { universitySlug, courseId, classId, pollId } = useParams<PollParams>();

  const { setSlideTitleAndSubtitle } = useAppContext();
  const { course } = useCourseContext();
  const { cls } = useClassContext();
  const { poll, setPoll } = usePollContext();

  const formMethods = useForm<FormValues>({
    defaultValues: {
      studentResponses: StudentResponses.ANONYMOUS,
      duration: ActivityDuration.NOT_APPLICABLE,
      allowLate: true,
      deadlineFirst: false,
      saveAsDefault: false,
    },
    mode: 'onSubmit',
  });

  const { setValue, reset } = formMethods;

  const onCloseDrawer = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!poll) return;

    const { duration, allowLate, deadlineFirst, subscribeToComments, saveAsDefault } = data;

    const dueDateType =
      poll.toBeDone === ToBeDone.PRE_CLASS
        ? DueDateType.AUTO
        : duration === ActivityDuration.MANUALLY_STOPPED
          ? DueDateType.MANUAL
          : DueDateType.AUTO;

    const activityDuration =
      poll.toBeDone === ToBeDone.PRE_CLASS ? ActivityDuration.NOT_APPLICABLE : duration;

    const isAnonymous = data.studentResponses === StudentResponses.ANONYMOUS;

    const payload = {
      classId: poll.classId,
      pollId: poll.id,
      publishPreferences: {
        toBeDone: poll.toBeDone,
        dueDateType,
        duration: activityDuration,
        allowLate,
        deadlineFirst,
        isAnonymous,
        subscribeToComments,
      },
      saveAsDefault,
    };

    if (isPublishing) {
      const { dueDateTime, dueDateType, publishedOn } = await publishPoll(payload);

      const updatedPoll: Poll = {
        ...poll,
        dueDateTime,
        publishedOn,
        preferences: {
          ...payload.publishPreferences,
          dueDateType,
        },
      };

      setPoll(updatedPoll);

      if (!(universitySlug && courseId && classId && pollId)) {
        return;
      }

      const baseUrl = process.env.REACT_APP_ACADLY_WEB_URL;

      const pollPageRelativeUrl = routes.poll.acadlyWebUrl({
        universitySlug,
        courseId: toShortId(courseId),
        classId: toShortId(classId),
        pollId: toShortId(pollId),
      });

      const pollPageUrl = `${baseUrl}${pollPageRelativeUrl}`;

      setSlideTitleAndSubtitle({
        title: t('acadly_activity_plugin_course_code', {
          ns: i18nNS.COMMON,
          courseCode: course?.code,
        }),
        subtitle: t('poll_was_published_for_lecture_scheduled_at_date_and_time_url', {
          ns: i18nNS.POLL,
          dateAndTime: format(cls?.startTime ?? 0, "hh:mm a 'on' do MMM, yyyy"),
          url: pollPageUrl,
        }),
      });
    } else {
      await savePublishPreferences(payload);

      const updatedPoll: Poll = {
        ...poll,
        preferences: payload.publishPreferences,
      };

      setPoll(updatedPoll);
    }

    onCloseDrawer();
  };

  const onUpdateStudentResponses = (studentResponses: StudentResponses) => {
    setValue('studentResponses', studentResponses);
  };

  const onUpdateDuration = (activityDuration: ActivityDuration) => {
    setValue('duration', activityDuration);
  };

  useEffect(
    function setInitialValues() {
      if (!open) return;
      if (!poll?.preferences) return;

      const { toBeDone, allowLate, duration, deadlineFirst, isAnonymous } = poll.preferences;

      setValue('studentResponses', isAnonymous ? StudentResponses.ANONYMOUS : StudentResponses.NOT_ANONYMOUS);
      if (toBeDone === ToBeDone.IN_CLASS) {
        setValue('duration', duration);
      } else {
        setValue('duration', ActivityDuration.NOT_APPLICABLE);
      }
      setValue('allowLate', allowLate);
      setValue('deadlineFirst', deadlineFirst);
      setValue('saveAsDefault', false);
    },
    [open, poll?.preferences, setValue]
  );

  return {
    cls,
    poll,
    formMethods,
    onCloseDrawer,
    onSubmit,
    onUpdateStudentResponses,
    onUpdateDuration,
  };
};

export default useEditPublishPreferencesDrawerVM;
